import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Spin,
  Table,
  Input,
  Tag,
  Modal,
  Tabs,
  Typography,
  List,
} from "antd";
import { useSelector } from "react-redux";
import {
  findAllUserEmailsPaginated,
  deleteUserEmailById,
  getAllMailUsers,
  manageAlias,
  findUserEmailById,
} from "@api/email/users/userEmail";
import {
  RiArrowGoBackFill,
  RiUserAddLine,
  RiDatabase2Line,
  RiMailSettingsLine,
  RiDashboardLine,
} from "react-icons/ri";
import { MailOutlined, SearchOutlined } from "@ant-design/icons";
import { EmailNotificationModal, MailInBoxUsers } from "@components";

// css
import "@assets/css/mail-in-a-box.css";
import "@assets/css/user-email-list.css";

const { Search } = Input;
const { TabPane } = Tabs;
const { Text } = Typography;

const UserEmailList = ({ history }) => {
  const { user } = useSelector((state) => state.auth);

  // State variables
  const [userEmails, setUserEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    totalUserEmails: 0,
  });

  // Alias states
  const [mailboxUsers, setMailboxUsers] = useState([]);
  const [mailboxLoading, setMailboxLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [aliasModalVisible, setAliasModalVisible] = useState(false);
  const [aliasInput, setAliasInput] = useState("");
  const [aliasLoading, setAliasLoading] = useState(false);

  // Notification modal states
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedEmailId, setSelectedEmailId] = useState(null);
  const [selectedEmailAddress, setSelectedEmailAddress] = useState("");
  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorModalVisible, setErrorModalVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Fetch user emails on component mount and when pagination/search changes
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const page = parseInt(searchParams.get("page") || "1", 10);
    const pageSize = parseInt(searchParams.get("pageSize") || "10", 10);
    const search = searchParams.get("search") || "";

    setSearchText(search);
    setPagination({ current: page, pageSize, totalUserEmails: 0 });
    fetchUserEmails(page, pageSize, search);
  }, []);

  // Modal handling functions
  const showSuccessModal = (message) => {
    setSuccessMessage(message);
    setSuccessModalVisible(true);
  };

  const showErrorModal = (message) => {
    setErrorMessage(message);
    setErrorModalVisible(true);
  };

  // Add function to fetch mailbox users
  const fetchMailboxUsers = async () => {
    setMailboxLoading(true);
    try {
      const response = await getAllMailUsers();
      setMailboxUsers(response.data.data);
      showSuccessModal("Successfully synced with Mail-in-a-Box");
    } catch (error) {
      console.error("Error fetching mailbox users:", error);
      showErrorModal("Failed to fetch mailbox users. Please try again.");
    } finally {
      setMailboxLoading(false);
    }
  };

  // Add useEffect to fetch mailbox users
  useEffect(() => {
    fetchMailboxUsers();
  }, []);

  // Function to fetch user emails
  const fetchUserEmails = async (page, pageSize, searchText = "") => {
    setLoading(true);
    try {
      const res = await findAllUserEmailsPaginated(page, pageSize, searchText);
      setUserEmails(res.data.userEmails || []);
      setPagination((p) => ({
        ...p,
        totalUserEmails: res.data.paginationData?.totalUserEmails,
        current: res.data.paginationData?.page,
        pageSize: res.data.paginationData?.perPage,
      }));
    } catch (err) {
      console.error("Fetch user emails error", err);
      showErrorModal("Failed to fetch user emails. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle pagination change
  const handlePaginationChange = (page, pageSize) => {
    const searchParams = new URLSearchParams();
    searchParams.set("page", page);
    searchParams.set("pageSize", pageSize);
    if (searchText) searchParams.set("search", searchText);

    history.push({ search: searchParams.toString() });
    fetchUserEmails(page, pageSize, searchText);
  };

  // Handle search
  const handleSearch = (searchValue) => {
    setSearchText(searchValue);
    setPagination({ ...pagination, current: 1 }); // Reset to first page
    fetchUserEmails(1, pagination.pageSize, searchValue);

    const searchParams = new URLSearchParams();
    searchParams.set("page", "1");
    searchParams.set("pageSize", pagination.pageSize);
    if (searchValue) searchParams.set("search", searchValue);
    history.push({ search: searchParams.toString() });
  };

  // Function to handle user email update
  const handleUserEmailUpdate = (id) => {
    history.push(`/super-admin/email/users/${id}/update`);
  };

  // Function to prepare for user email delete
  const handleUserEmailDelete = (id, email) => {
    setSelectedEmailId(id);
    setSelectedEmailAddress(email);
    setDeleteModalVisible(true);
  };

  // Function to confirm user email deletion
  const confirmUserEmailDelete = async () => {
    try {
      await deleteUserEmailById(selectedEmailId);
      showSuccessModal(
        `Email user "${selectedEmailAddress}" has been deleted successfully`
      );

      // Refresh the user email list after a short delay
      setTimeout(() => {
        fetchUserEmails(pagination.current, pagination.pageSize, searchText);
      }, 1000);
    } catch (error) {
      console.error("Error deleting user email", error);
      showErrorModal(
        error.response?.data?.message ||
          "Failed to delete user email. Please try again."
      );
    }
  };

  // Helper function to extract domain from email
  const extractDomain = (email) => {
    if (!email) return "";
    const parts = email.split("@");
    return parts.length > 1 ? parts[1] : "";
  };

  // Add alias management functions
  const handleAliasManagement = (user) => {
    setSelectedUser(user);
    setAliasModalVisible(true);
  };

  const handleAddAlias = async () => {
    if (!aliasInput.trim()) return;

    setAliasLoading(true);
    try {
      await manageAlias.add(selectedUser._id, aliasInput.trim());
      const updatedUser = await findUserEmailById(selectedUser._id);
      setSelectedUser(updatedUser.data);
      setAliasInput("");
      showSuccessModal("Alias added successfully");
    } catch (error) {
      showErrorModal(error.message || "Failed to add alias");
    } finally {
      setAliasLoading(false);
    }
  };

  const handleRemoveAlias = async (alias) => {
    setAliasLoading(true);
    try {
      await manageAlias.remove(selectedUser._id, alias);
      const updatedUser = await findUserEmailById(selectedUser._id);
      setSelectedUser(updatedUser.data);
      showSuccessModal("Alias removed successfully");
    } catch (error) {
      showErrorModal(error.message || "Failed to remove alias");
    } finally {
      setAliasLoading(false);
    }
  };

  // Table columns configuration
  const columns = [
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (email) => <span className="email-cell">{email}</span>,
    },
    {
      title: "Domain",
      key: "domain",
      render: (_, record) => (
        <span className="email-domain-cell">{extractDomain(record.email)}</span>
      ),
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name) => <span className="email-name-cell">{name}</span>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag
          className={`email-status-tag email-status-${status.toLowerCase()}`}
        >
          {status}
        </Tag>
      ),
    },
    {
      title: "Admin",
      dataIndex: "isAdmin",
      key: "isAdmin",
      render: (isAdmin) =>
        isAdmin ? <span className="email-admin-badge">Admin</span> : "No",
    },
    {
      title: "Quota Used",
      key: "quota",
      render: (_, record) => (
        <span className="email-quota-cell">
          {`${(record.quotaUsed / 1024).toFixed(2)}/${(
            record.quotaLimit / 1024
          ).toFixed(2)} GB`}
        </span>
      ),
    },
    {
      title: "Aliases",
      key: "aliases",
      render: (_, record) => (
        <Button
          type="link"
          onClick={() => handleAliasManagement(record)}
          className="email-alias-button"
        >
          Manage Aliases ({record.aliases?.length || 0})
        </Button>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <>
          <Button
            type="primary"
            size="small"
            className="email-table-action email-table-edit"
            style={{ marginRight: "10px" }}
            onClick={() => handleUserEmailUpdate(record._id)}
          >
            Edit
          </Button>
          <Button
            type="danger"
            size="small"
            className="email-table-action email-table-delete"
            onClick={() => handleUserEmailDelete(record._id, record.email)}
          >
            Delete
          </Button>
        </>
      ),
    },
  ];

  // Render the alias management modal with custom notifications
  const renderAliasModal = () => (
    <Modal
      title={`Manage Aliases for ${selectedUser?.email}`}
      visible={aliasModalVisible}
      onCancel={() => setAliasModalVisible(false)}
      footer={null}
      width={600}
      className="email-alias-modal"
    >
      <Spin spinning={aliasLoading}>
        <div className="email-alias-input-group">
          <Input.Group compact>
            <Input
              style={{ width: "calc(100% - 100px)" }}
              value={aliasInput}
              onChange={(e) => setAliasInput(e.target.value)}
              placeholder="Enter new alias email"
              onPressEnter={handleAddAlias}
            />
            <Button type="primary" onClick={handleAddAlias}>
              Add Alias
            </Button>
          </Input.Group>
        </div>

        <List
          className="email-alias-list"
          dataSource={selectedUser?.aliases || []}
          renderItem={(alias) => (
            <List.Item
              actions={[
                <Button
                  type="link"
                  danger
                  onClick={() => handleRemoveAlias(alias)}
                  className="email-alias-remove-button"
                >
                  Remove
                </Button>,
              ]}
            >
              <List.Item.Meta avatar={<MailOutlined />} title={alias} />
            </List.Item>
          )}
          locale={{ emptyText: "No aliases configured" }}
        />
      </Spin>
    </Modal>
  );

  return (
    <div className="email-list-container">
      <Card className="email-list-card">
        <div className="email-list-header">
          <Button
            type="link"
            icon={<RiArrowGoBackFill style={{ fontSize: "1.5em" }} />}
            onClick={() => history.push("/super-admin/email/email-campaigns")}
            className="email-list-back-button"
          />

          <h3 className="email-list-title">User Email Management</h3>

          {/* Action Buttons */}
          <div className="email-list-actions">
            <Button
              type="dashed"
              icon={<RiDashboardLine />}
              onClick={() => history.push("/super-admin/email/email-campaigns")}
              className="email-action-button email-action-button-campaigns"
            >
              <span>View Campaigns</span>
            </Button>

            <Button
              type="primary"
              icon={<RiUserAddLine />}
              onClick={() => history.push("/super-admin/email/users/create")}
              className="email-action-button email-action-button-create"
            >
              <span>Create User</span>
            </Button>

            <Button
              type="primary"
              icon={<RiDatabase2Line />}
              onClick={() => history.push("/super-admin/email/domain")}
              className="email-action-button email-action-button-manage"
            >
              <span>Manage Domain</span>
            </Button>

            <Button
              type="primary"
              icon={<RiMailSettingsLine />}
              onClick={() => history.push("/super-admin/email/smtp")}
              className="email-action-button email-action-button-manage"
            >
              <span>Manage SMTP</span>
            </Button>

            <Search
              placeholder="Search by email/name"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={handleSearch}
              className="email-search"
              enterButton={<SearchOutlined />}
            />
          </div>
        </div>

        <Tabs defaultActiveKey="1" className="email-tabs">
          <TabPane tab="User Emails" key="1">
            {loading ? (
              <div className="email-loading">
                <Spin size="large" />
              </div>
            ) : (
              <div className="email-table">
                <Table
                  columns={columns}
                  dataSource={userEmails}
                  pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.totalUserEmails,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total) => `Total ${total} items`,
                  }}
                  onChange={({ current, pageSize }) =>
                    handlePaginationChange(current, pageSize)
                  }
                  rowKey={(record) => record._id}
                  scroll={{ x: "max-content" }}
                />
              </div>
            )}
          </TabPane>

          <TabPane tab="Mail-in-a-Box Users" key="2" className="mail-box-tab">
            <div
              className="mail-box-wrapper"
              style={{ height: "calc(100vh - 240px)", overflow: "auto" }}
            >
              <MailInBoxUsers
                mailboxUsers={mailboxUsers}
                loading={mailboxLoading}
                onSync={fetchMailboxUsers}
              />
            </div>
          </TabPane>
        </Tabs>
      </Card>

      {selectedUser && renderAliasModal()}

      {/* Delete Confirmation Modal */}
      <EmailNotificationModal
        isOpen={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        type="delete"
        title="Delete Email User"
        message={`Are you sure you want to delete the email user "${selectedEmailAddress}"? This action will also remove the user from Mail-in-a-Box and cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmUserEmailDelete}
      />

      {/* Success Notification Modal */}
      <EmailNotificationModal
        isOpen={successModalVisible}
        onClose={() => setSuccessModalVisible(false)}
        type="success"
        title="Success"
        message={successMessage}
        autoCloseTime={3000}
      />

      {/* Error Notification Modal */}
      <EmailNotificationModal
        isOpen={errorModalVisible}
        onClose={() => setErrorModalVisible(false)}
        type="error"
        title="Error"
        message={errorMessage}
      />
    </div>
  );
};

export default UserEmailList;
